// Pastel texture asset for `pen='pastel'` — static fine-powder-grain alpha
// mask (phase11-light-pens-2026-08-21.md §2).
//
// ## Why alpha-multiply, not threshold (§2.1)
//
// Pastel rides the SAME alpha-multiply seam every `pen='texture'` preset
// uses (`createTextureStampRenderer`'s kernel path, texture-brush-design.md
// §2) — the per-texel alpha `t ∈ [0,1]` is folded into the per-stamp value at
// the single quantisation point. Unlike crayon's binary threshold, EVERY
// texel carries at least a little alpha, so repeated overlapping strokes
// saturate every texel toward full coverage — this is what makes pastel
// "重ねると埋まる" / usable as a solid fill (§2.1).
//
// ## Why this doesn't "average away" like airbrush's dab noise (§2.3 — read
// this before touching the constants)
//
// `airbrush-design §2.1`'s "乗算は平均化して粒が消える" warning is about a
// kernel that re-rolls a RANDOM per-dab pattern (a fresh hash draw every
// stamp) — many independent random draws over the same pixel average toward
// a flat mid-grey, which is why airbrush needed a threshold-dither instead of
// alpha-multiply. This file is NOT that: it is a STATIC world-mapped array,
// so every stamp that lands on world pixel (x,y) samples the EXACT SAME texel
// value every time. There is no independent redraw to average over — the
// grain persists no matter how many times the same spot is stamped. The
// existing 紙 (paper) preset is the working proof of this: it's already a
// static world alpha-multiply map and its grain does not wash out under
// build-up.
//
// ## ⚠️ …but a DIFFERENT mechanism DID wash it out (§2.3, 2026-08-21 実機)
//
// The paragraph above is still correct — there is no averaging. But a static
// per-texel value fed into `accumulate='add'` (source-over, `out = in +
// s·(1-in)`) is NOT the same thing as "no washout": the SAME texel value
// gets compounded through that formula roughly `2r` times per stroke (every
// dab within one sweep touches it again), and `1-(1-s·t)^N` is a compression
// function — it flattens this map's texel-to-texel range (0.64〜0.82) toward
// a near-uniform ceiling well before N≈40, regardless of amplitude tuning.
// This is what made the grain invisible in practice even though "紙" (which
// build-up leaves ON, unlike pastel) never showed the same failure — 紙 never
// needed its texel differences to stay visible WITHIN one stroke the way
// pastel's "粉" character does; a slightly-flattened-but-still-textured paper
// grain still reads as paper. The fix (`createPastelStampRenderer`'s doc,
// `strokeRenderer.ts`'s `isFlatPen`, `rasterizer.ts`'s `perStampAlphaFor`
// pastel case) is NOT a map/amplitude change — it's switching pastel off
// build-up entirely: `accumulate='max'` + a LINEAR (not power-curve)
// per-stamp alpha, so the per-pixel value is computed once (`strokeAlpha ×
// t/255`) and frozen, never compounded. Cross-stroke "重ねると埋まる" still
// works because separate strokes each commit their own buffer onto
// `layer.main` via source-over.
//
// ## Visual intent (§2.5): fine, high-frequency, lower-contrast than 紙
//
// 紙 (`paperTexture.ts`) uses a base=220 / ±30 dither / period-8 ripple tuned
// to read as "nearly solid with visible tooth". Pastel wants the "粉" (powder)
// character — a lower base with a WIDER texel range, so the grain itself is
// the dominant read rather than a faint tooth over a solid fill.
//
// ## Grain knob (3 pre-baked levels, no per-pixel kernel — §2.6 / §5)
//
// `penParams.pastel.grain` (0..2) picks one of 3 pre-baked maps with
// different **粒 (noise cell) sizes** (1/2/4 px). Preset selection, not a
// runtime kernel parameter — mirrors `crayonThreshold.ts`'s grain knob and
// `texturePresetIndex`'s existing preset-selection pattern.
//
// **旧実装は ripple の周期 (2/4/8) を振っていたが、実機で 3 段階が判別不能**
// だった (2026-08-21): 支配的なのは per-texel (1px) の dither で、ripple を
// 変えても粒の見え方が動かない。粒のセルサイズを振る形に作り替えた。
//
// **セルを粗くしてもモザイクにならない理由**: クレヨンの教訓 (§2.5.3) を適用し、
// **1px の細かい成分を常に加算**している。粗いセルだけにすると軸整列した正方形が
// 剥き出しになる。
//
// ## Determinism (ABSOLUTE — texture-brush-design.md §3, inherited here)
//
// Fixed integer formula only, no `Math.random`, no float transcendentals.
// 256×256, 粒 cell size divides 256 so every grain level tiles seamlessly.
export const PASTEL_TEXTURE_SIZE = 256;
/**
 * 粒 (noise cell) sizes (px) per grain level — finest at the lowest index.
 * All divide `PASTEL_TEXTURE_SIZE` (256) so every level tiles seamlessly.
 */
export const PASTEL_GRAIN_CELL_SIZES = [1, 2, 4];
/** Default grain index (mid frequency) when `penParams.grain` is omitted. */
export const PASTEL_DEFAULT_GRAIN = 1;
/**
 * Deterministic integer hash → pseudo-random in `[0, 2^32)`. Same
 * construction as `paperTexture.ts`'s `hash2`, with its own salt so pastel's
 * dither is independent of paper's / crayon's at the same `(x, y)`.
 */
const hash2 = (x, y) => {
    let h = (Math.imul(x, 0x1656_67b1) ^ Math.imul(y, 0x2545_f491)) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x85eb_ca6b) >>> 0;
    h = Math.imul(h ^ (h >>> 13), 0xc2b2_ae35) >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    return h;
};
const clamp255 = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);
/** 中央値 (2026-08-21 実機で 190 → 150。粒を見せるにはコントラストの余地が要る)。 */
const PASTEL_BASE_ALPHA = 150;
/** 粒 (grain cell) 成分の振幅。 */
const PASTEL_COARSE_AMP = 50;
/** 常在する 1px 成分の振幅 (モザイク防止、§2.5.3)。 */
const PASTEL_FINE_AMP = 25;
/** 1px 成分の salt (粒成分と同じ hash を使うためのずらし)。 */
const PASTEL_FINE_SALT = 7777;
/**
 * Build one grain level's `size × size` alpha mask: a mid base brightness +
 * a **粒 cell** noise (amplitude `PASTEL_COARSE_AMP`, cell = the grain level)
 * + an **always-present 1px** noise (`PASTEL_FINE_AMP`).
 *
 * ## Why the amplitudes are this large (2026-08-21 実機)
 *
 * 旧実装は base=190 / ±18 で、単一パスの alpha 幅が **40〜49 (255 中 9)** しか
 * なく実機でほぼ平坦に見えていた。alpha 乗算 seam は per-stamp 値に texel を
 * 掛けるだけなので、**texel のコントラストがそのまま見え方のコントラスト**に
 * なる。粉として見えるにはこの幅が要る。
 *
 * (それ以前は `accumulate='add'` による飽和圧縮で振幅を上げても無意味だった。
 * flat/max cadence に移した後で初めて振幅の調整が効くようになった — §2.3 の
 * 訂正ブロック参照。)
 */
const buildPastelAlpha = (size, cellSize) => {
    const out = new Uint8Array(size * size);
    const base = PASTEL_BASE_ALPHA;
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            // 粒 (grain-level cell) + 常在する 1px 成分。後者を必ず混ぜることで、
            // セルを粗くしても軸整列した正方形が剥き出しにならない (§2.5.3 の教訓)。
            const coarse = (hash2(Math.floor(x / cellSize), Math.floor(y / cellSize)) % (PASTEL_COARSE_AMP * 2 + 1)) -
                PASTEL_COARSE_AMP;
            const fine = (hash2(x + PASTEL_FINE_SALT, y + 31) % (PASTEL_FINE_AMP * 2 + 1)) - PASTEL_FINE_AMP;
            out[y * size + x] = clamp255(base + coarse + fine);
        }
    }
    return out;
};
/** One pre-baked `TextureSampler` per grain level, built once at module load. */
export const PASTEL_TEXTURE_SAMPLERS = PASTEL_GRAIN_CELL_SIZES.map((cellSize) => ({
    mapping: 'world',
    width: PASTEL_TEXTURE_SIZE,
    height: PASTEL_TEXTURE_SIZE,
    alpha: buildPastelAlpha(PASTEL_TEXTURE_SIZE, cellSize),
    scale: 1,
}));
/**
 * Clamp/quantise a wire-level `grain` (from `penParams.pastel.grain`,
 * optional) into a valid index of `PASTEL_TEXTURE_SAMPLERS` /
 * `PASTEL_GRAIN_CELL_SIZES`. `undefined` → `PASTEL_DEFAULT_GRAIN`.
 * Out-of-range values clamp (defensive — the protocol schema already rejects
 * them at intake, this is the render-side belt).
 */
export const pastelGrainIndex = (grain) => {
    const g = Math.trunc(grain ?? PASTEL_DEFAULT_GRAIN);
    const max = PASTEL_GRAIN_CELL_SIZES.length - 1;
    return g < 0 ? 0 : g > max ? max : g;
};
//# sourceMappingURL=pastelTexture.js.map